'use client';
import Link from 'next/link';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { supabaseBrowser } from '@/lib/supabaseBrowser';

type Mode = 'signin' | 'signup' | 'magic';

function LoginForm() {
  const supabase = supabaseBrowser();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>('signin');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Set only when signInWithPassword succeeds AND the account has 2FA
  // enrolled -- holds what's needed to complete the second-factor check
  // before actually redirecting into the app.
  const [mfaChallenge, setMfaChallenge] = useState<{ factorId: string; challengeId: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  useEffect(() => {
    const m = searchParams.get('mode');
    if (m === 'signup') setMode('signup');
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSent(false);
    setBusy(true);
    try {
      if (mode === 'magic') {
        const redirectTo = `${window.location.origin}/`;
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: redirectTo },
        });
        if (error) throw error;
        setSent(true);
      } else if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        // Password alone gets you an AAL1 session. If this account has a
        // verified second factor, Supabase's nextLevel will say aal2 is
        // required -- in that case we stop here and show the code prompt
        // instead of redirecting, rather than letting a password-only
        // session into the app.
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aal && aal.nextLevel === 'aal2' && aal.currentLevel !== aal.nextLevel) {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const factor = factors?.totp?.[0];
          if (factor) {
            const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
            if (challengeError) throw challengeError;
            setMfaChallenge({ factorId: factor.id, challengeId: challenge.id });
            setBusy(false);
            return;
          }
        }

        window.location.href = '/';
        return;
      } else if (mode === 'signup') {
        if (!agreed) {
          setError('Please confirm you are 18 or older and agree to the Terms and Privacy Policy.');
          setBusy(false);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/onboarding` },
        });
        if (error) throw error;
        // Fire-and-forget -- logs the signup form being submitted successfully.
        // Deliberately separate from onboarding_completed (logged on the
        // /onboarding page) since not everyone who signs up finishes that step --
        // the gap between these two numbers is the real drop-off to watch.
        fetch('/api/log-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventType: 'signup' }),
        }).catch(() => {});
        setSent(true);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  async function verifyMfaLogin() {
    if (!mfaChallenge || mfaCode.length !== 6) return;
    setMfaError(null);
    setMfaBusy(true);
    const { error } = await supabase.auth.mfa.verify({
      factorId: mfaChallenge.factorId,
      challengeId: mfaChallenge.challengeId,
      code: mfaCode,
    });
    setMfaBusy(false);
    if (error) {
      setMfaError('Incorrect code — check your authenticator app and try again.');
      return;
    }
    window.location.href = '/';
  }

  return (
    <section className="card" style={{ padding: '32px 28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header */}
      <div>
        <h1 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '28px', color: 'var(--text)', marginBottom: '6px' }}>
          {mode === 'signup' ? 'Join NoiseGate.' : 'Welcome back.'}
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          {mode === 'signin' && 'Sign in with your email and password.'}
          {mode === 'signup' && 'Create an account to post and respond.'}
          {mode === 'magic' && "We'll send you a link — no password needed."}
        </p>
      </div>

      {/* Mode toggle — sign in + sign up only */}
      {mode !== 'magic' && (
        <div style={{ display: 'flex', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: '10px', padding: '3px', gap: '3px' }}>
          {(['signin', 'signup'] as Mode[]).map(m => (
            <button key={m} onClick={() => { setMode(m); setError(null); setSent(false); setAgreed(false); }} style={{
              flex: 1, padding: '7px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
              background: mode === m ? 'var(--surface)' : 'transparent',
              color: mode === m ? 'var(--text)' : 'var(--text-muted)',
              border: mode === m ? '1px solid var(--border)' : '1px solid transparent',
              fontFamily: 'DM Sans, sans-serif',
            }}>
              {m === 'signin' ? '🔑 Sign in' : '🌱 Sign up'}
            </button>
          ))}
        </div>
      )}

      {/* Magic link back button */}
      {mode === 'magic' && (
        <button onClick={() => { setMode('signin'); setError(null); setSent(false); }} style={{
          alignSelf: 'flex-start', fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontFamily: 'DM Sans, sans-serif',
        }}>
          ← Back to sign in
        </button>
      )}

      {/* Form */}
      {mfaChallenge ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Enter the 6-digit code from your authenticator app.
          </p>
          <div className="field">
            <label className="label" htmlFor="mfa-login-code">6-digit code</label>
            <input
              id="mfa-login-code"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input"
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              autoFocus
            />
          </div>
          {mfaError && (
            <div style={{ fontSize: '13px', color: 'var(--danger)', background: 'rgba(196,122,122,0.08)', border: '1px solid rgba(196,122,122,0.2)', borderRadius: '8px', padding: '10px 12px' }}>
              {mfaError}
            </div>
          )}
          <button
            onClick={verifyMfaLogin}
            disabled={mfaBusy || mfaCode.length !== 6}
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '10px', marginTop: '4px' }}
          >
            {mfaBusy ? 'Verifying…' : 'Verify'}
          </button>
        </div>
      ) : !sent ? (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div className="field">
            <label className="label" htmlFor="login-email">Email</label>
            <input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="input" placeholder="you@example.com" required />
          </div>

          {mode !== 'magic' && (
            <div className="field">
              <label className="label" htmlFor="login-password">Password</label>
              <input id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="input" placeholder="••••••••" required minLength={6} />
              {mode === 'signup' && (
                <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic' }}>At least 6 characters.</div>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginTop: '4px' }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ marginTop: '3px', accentColor: 'var(--green)', flexShrink: 0 }}
              />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                I confirm I am 18 years of age or older and agree to the{' '}
                <Link href="/terms" style={{ color: 'var(--indigo)' }}>Terms of Service</Link>{' '}
                and{' '}
                <Link href="/privacy" style={{ color: 'var(--indigo)' }}>Privacy Policy</Link>.
              </span>
            </label>
          )}

          {error && (
            <div style={{ fontSize: '13px', color: 'var(--danger)', background: 'rgba(196,122,122,0.08)', border: '1px solid rgba(196,122,122,0.2)', borderRadius: '8px', padding: '10px 12px' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={busy || (mode === 'signup' && !agreed)} className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '10px', marginTop: '4px' }}>
            {busy ? 'Working…' : mode === 'magic' ? 'Send magic link' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>

          {mode !== 'magic' && (
            <div style={{ textAlign: 'center', paddingTop: '4px' }}>
              <button
                type="button"
                onClick={() => { setMode('magic'); setError(null); setSent(false); }}
                style={{ fontSize: '12px', color: 'var(--indigo)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' }}
              >
                Or sign in with a magic link — no password needed
              </button>
            </div>
          )}
        </form>
      ) : (
        <div style={{ background: 'var(--green-glow)', border: '1px solid var(--green-dim)', borderRadius: '8px', padding: '16px', fontSize: '13px', color: 'var(--green)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontWeight: 600, fontSize: '15px' }}>✉️ Check your email</div>
          <div style={{ color: 'var(--text-muted)' }}>
            {mode === 'signup'
              ? <>We sent a confirmation link to <b style={{ color: 'var(--text)' }}>{email}</b>. Didn&apos;t get it? You may already have an account — <button onClick={() => { setSent(false); setMode('signin'); }} style={{ color: 'var(--indigo)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: 0 }}>try signing in instead.</button></>
              : <>We sent a magic link to <b style={{ color: 'var(--text)' }}>{email}</b>. Click it to sign in.</>
            }
          </div>
          <button onClick={() => setSent(false)} style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start', padding: 0, marginTop: '4px' }}>
            ← Try a different email
          </button>
        </div>
      )}

      {/* Helper note */}
      <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', textAlign: 'center' }}>
        {mode === 'signin' && <>Don&apos;t have an account? <button onClick={() => { setMode('signup'); setError(null); setSent(false); }} style={{ color: 'var(--indigo)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: 0 }}>Sign up.</button></>}
        {mode === 'signup' && <>Already have an account? <button onClick={() => { setMode('signin'); setError(null); setSent(false); }} style={{ color: 'var(--indigo)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: 0 }}>Sign in.</button></>}
        {mode === 'magic' && 'Magic link expires after 1 hour.'}
      </p>

    </section>
  );
}

export default function LoginPage() {
  return (
    <main style={{ maxWidth: '420px', margin: '60px auto', padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Suspense fallback={null}>
        <LoginForm />
      </Suspense>
    </main>
  );
}
